import React, { useEffect, useState } from "react";
import { Autocomplete, Card, Grid, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import { postAPICall } from "lib/callApi";
import { PERFORMANCE_ANALYTICS_OPTIONS, FILTER_PARAMS } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import { generateScatterChartData } from "utils/PerformanceHelperFunctions";
import { fetchPerformanceAnalytics } from "apis/performance";
import BaseModal from "components/molecules/Modals/BaseModal";
import ScatterChart from "../ScatterChart";
import FilterAutocomplete from "../FilterAutocomplete";

const PerformanceAnalytics = ({ activeUserId }) => {
  const qparams = new URLSearchParams(window.location.search);
  const [performanceAnalyticsPeriodDisplay, setPerformanceAnalyticsPeriodDisplay] =
    useState("Current Quarter");
  const [performanceAnalyticsParams, setPerformanceAnalyticsParams] = useState({
    timeframe: qparams?.get("performanceAnalyticsTimeframe") || "Quarter",
    offset: 0,
  });
  const [performanceAnalyticsFilterOpen, setPerformanceAnalyticsFilterOpen] = useState(false);
  const [scatterData, setScatterData] = useState([]);
  const [modalFilters, setModalFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState({});

  const handleDeleteClick = (modalFilterKey, newValue) => {
    setModalFilters((prev) => ({
      ...prev,
      [modalFilterKey]: newValue,
    }));
  };

  const performanceAnalyticsTimeframe = performanceAnalyticsParams.timeframe;
  const performanceAnalyticsOffset = performanceAnalyticsParams.offset;
  const stringifiedModalFilters = JSON.stringify(modalFilters);

  const resyncChartData = async () => {
    try {
      const response = await fetchPerformanceAnalytics(
        performanceAnalyticsTimeframe,
        performanceAnalyticsOffset,
        modalFilters
      );

      if (response?.status === 200) {
        if (response?.data?.data?.scatterData?.length) {
          setScatterData(generateScatterChartData(response.data.data.scatterData, activeUserId));
          setPerformanceAnalyticsPeriodDisplay(response.data.data.dateRangeDisplay);
        } else {
          setScatterData([]);
        }
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              response?.response?.data?.errorMessage ||
              response.message ||
              "Failed to retrieve performance analytics data",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (activeUserId) {
      resyncChartData();
    }
  }, [
    performanceAnalyticsTimeframe,
    performanceAnalyticsOffset,
    stringifiedModalFilters,
    activeUserId,
  ]);

  const syncOptionsAndFilter = async (_filters) => {
    try {
      setIsLoading(true);
      const response = await postAPICall(FILTER_PARAMS, true, {
        filters: {
          filterLocations: _filters?.location?.map((lo) => lo.id) || [],
          filterTeams: _filters?.team?.map((lo) => lo.id) || [],
          filterRoles: _filters?.role?.map((lo) => lo.id) || [],
          filterUsers: _filters?.user?.map((lo) => lo.userCompanyLinkId) || [],
          filterReportsTo: _filters?.reportsTo?.map((lo) => lo.id) || [],
        },
      });
      if (response?.data?.success) {
        const user = response.data.data.users;
        const { reportsTo } = response.data.data;
        const role = response.data.data.roles;
        const team = response.data.data.teams;
        const teamLocation = response.data.data.locations;
        const optionObj = {
          team,
          location: teamLocation,
          user,
          role,
          reportsTo,
        };
        const updatedFilter = {};
        Object.keys(optionObj).forEach((key) => {
          if (Array.isArray(optionObj[key]) && Array.isArray(_filters[key])) {
            const arr1 = optionObj[key].map((obj) => obj.id);
            const arr2 = _filters[key];
            updatedFilter[key] = arr2.filter((arr2Obj) => arr1.includes(arr2Obj.id));
          }
        });

        setModalFilters({ ...updatedFilter });
        setOptions(optionObj);
      } else if (response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              response?.response?.data?.message || response.message || "Could not retrieve data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (performanceAnalyticsFilterOpen) {
      syncOptionsAndFilter(modalFilters);
    }
  }, [performanceAnalyticsFilterOpen]);

  return (
    <>
      <Card
        sx={{
          p: "1rem",
          height: "600px",
          mt: "24px",
        }}
        id="performanceAnalyticsCard"
      >
        <MDBox
          sx={{
            position: "relative",
          }}
        >
          <MDTypography
            variant="h3"
            sx={{
              textAlign: "center",
            }}
          >
            Performance Analytics
          </MDTypography>
          <Grid item xs={3} md={2}>
            <MDButton
              sx={{ minWidth: "100%", borderRadius: "2rem" }}
              color="primaryRed"
              onClick={() => setPerformanceAnalyticsFilterOpen(true)}
            >
              Filter
            </MDButton>
          </Grid>
          <Autocomplete
            disablePortal
            disableClearable
            id="performance-analytics-timeframe-select"
            value={performanceAnalyticsParams.timeframe}
            onChange={(_, newTimeframe) => {
              setPerformanceAnalyticsParams({
                timeframe: newTimeframe,
                offset: 0,
              });
            }}
            options={PERFORMANCE_ANALYTICS_OPTIONS}
            renderInput={(params) => <TextField {...params} label="Timeframe" />}
            sx={{
              position: "absolute",
              right: 0,
              width: "130px",
              top: 0,
            }}
          />
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ArrowLeft
              style={{ cursor: "pointer" }}
              onClick={() =>
                setPerformanceAnalyticsParams((prev) => ({
                  ...prev,
                  offset: prev.offset - 1,
                }))
              }
              sx={{
                fontSize: "20px",
                marginRight: "16px",
                color: "black",
              }}
              size="large"
            />
            <MDTypography
              variant="h6"
              sx={{
                textAlign: "center",
                fontWeight: "400",
              }}
            >
              {performanceAnalyticsPeriodDisplay}
            </MDTypography>
            <ArrowRight
              style={{ cursor: "pointer" }}
              onClick={() =>
                setPerformanceAnalyticsParams((prev) => ({
                  ...prev,
                  offset: Math.min(0, prev.offset + 1),
                }))
              }
              disabled={!performanceAnalyticsParams.offset}
              sx={{
                fontSize: "10px",
                marginLeft: "16px",
                color: performanceAnalyticsParams.offset ? "black" : "#0000004a",
              }}
              size="large"
            />
          </MDBox>
        </MDBox>
        <MDBox sx={{ height: "500px" }}>
          <ScatterChart data={scatterData} />
        </MDBox>
      </Card>
      <BaseModal
        open={performanceAnalyticsFilterOpen}
        setOpen={setPerformanceAnalyticsFilterOpen}
        onConfirmCb={resyncChartData}
        title="Performance Analytics Filters"
        cancelLabel="Close"
        confirmLabel="Apply"
      >
        <MDBox mb={2} sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Loader loading={isLoading} />
          <FilterAutocomplete
            field="location"
            options={options.location || []}
            label="Location"
            value={modalFilters?.location}
            onApplyClick={() => null}
            onDeleteClick={handleDeleteClick}
            onChange={(_, filter) => {
              setModalFilters((prev) => ({ ...prev, location: filter }));
            }}
          />

          <FilterAutocomplete
            options={options.team || []}
            label="Team"
            field="team"
            value={modalFilters.team || []}
            onApplyClick={() => null}
            onDeleteClick={handleDeleteClick}
            onChange={(_, filter) => {
              setModalFilters((prev) => ({ ...prev, team: filter }));
            }}
          />
          <FilterAutocomplete
            label="Role"
            field="role"
            value={modalFilters.role || []}
            onApplyClick={() => null}
            onDeleteClick={handleDeleteClick}
            onChange={(_, filter) => {
              setModalFilters((prev) => ({ ...prev, role: filter }));
            }}
            options={options?.role || []}
          />
          <FilterAutocomplete
            label="User"
            field="user"
            value={modalFilters?.user || []}
            onApplyClick={() => null}
            onDeleteClick={handleDeleteClick}
            onChange={(_, filter) => {
              setModalFilters((prev) => ({ ...prev, user: filter }));
            }}
            options={options?.user || []}
          />
          <FilterAutocomplete
            label="Reports To"
            field="reportsTo"
            value={modalFilters?.reportsTo?.length ? modalFilters.reportsTo : []}
            onApplyClick={() => null}
            onDeleteClick={handleDeleteClick}
            onChange={(_, filter) => {
              setModalFilters((prev) => ({ ...prev, reportsTo: filter }));
            }}
            options={options?.reportsTo?.length ? options?.reportsTo : []}
          />
        </MDBox>
      </BaseModal>
    </>
  );
};

export default PerformanceAnalytics;
